"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { MANN_WHITNEY_U_TEST_CALCULATOR_STRINGS } from "@/components/tools/mann-whitney-u-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import { median } from "@/lib/stats/descriptive";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import {
  EXACT_LIMIT,
  type MannWhitneyResult,
} from "@/lib/tool-kit/stats-tests/mann-whitney";

/** Results for the Mann–Whitney U calculator. */

type Strings = typeof MANN_WHITNEY_U_TEST_CALCULATOR_STRINGS;

const MAX_RANK_ROWS = 40;

function methodNote(
  r: MannWhitneyResult,
  continuity: boolean,
  strings: Strings,
  fmt: NumberFormatter
): string {
  if (r.method === "exact") {
    return t(strings.methodExact, { p: formatP(r.pNormal, fmt) });
  }
  const bySize = !r.hasTies && (r.n1 > EXACT_LIMIT || r.n2 > EXACT_LIMIT);
  if (bySize) {
    return continuity ? strings.methodSizeCc : strings.methodSize;
  }
  return continuity ? strings.methodTiesCc : strings.methodTies;
}

function RankTable({
  r,
  x,
  y,
  strings,
  fmt,
}: {
  r: MannWhitneyResult;
  x: readonly number[];
  y: readonly number[];
  strings: Strings;
  fmt: NumberFormatter;
}) {
  if (x.length + y.length > MAX_RANK_ROWS) {
    return <p className={HINT}>{strings.ranksHidden}</p>;
  }
  const rows = [
    ...x.map((value, i) => ({
      value,
      group: 1,
      rank: r.ranks1[i],
      id: `a${i}`,
    })),
    ...y.map((value, i) => ({
      value,
      group: 2,
      rank: r.ranks2[i],
      id: `b${i}`,
    })),
  ].sort((a, b) => a.value - b.value);
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.ranksHeading}</h3>
      <p className={HINT}>{strings.ranksHint}</p>
      <ResultTable
        head={[strings.sample, strings.value, strings.rank]}
        rows={rows.map((row) => ({
          key: row.id,
          cells: [
            fmt(row.group),
            formatStat(row.value, fmt),
            formatStat(row.rank, fmt, 1),
          ],
        }))}
      />
    </>
  );
}

export function MannWhitneyResults({
  r,
  x,
  y,
  continuity,
  alpha,
  strings,
  fmt,
}: {
  r: MannWhitneyResult;
  x: readonly number[];
  y: readonly number[];
  continuity: boolean;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const big = r.n1 + r.n2;
  const half = (r.n1 * (r.n1 + 1)) / 2;
  const cc = continuity && r.u < r.mu ? " + 0.5" : "";
  const apa = richTemplate(strings.apaTemplate, {
    u: formatStat(r.u, fmt, 1),
    z: fixed(r.z, 2, fmt),
    p: apaP(r.p, fmt),
    r: fixed(r.r, 2, fmt, false),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.uStatistic}
          value={formatStat(r.u, fmt, 1)}
        />
        <StatCard
          emphasis
          label={r.method === "exact" ? strings.pExact : strings.pNormal}
          value={formatP(r.p, fmt)}
        />
        <StatCard label={strings.zScore} value={n(r.z)} />
        <StatCard label={strings.effectR} value={n(r.r)} />
        <StatCard label={strings.rankBiserial} value={n(r.rankBiserial)} />
        <StatCard
          label={strings.meanRanks}
          value={`${formatStat(r.meanRank1, fmt, 2)}, ${formatStat(r.meanRank2, fmt, 2)}`}
        />
        <StatCard
          label={strings.medians}
          value={`${formatStat(median(x), fmt, 2)}, ${formatStat(median(y), fmt, 2)}`}
        />
      </StatGrid>
      <p className={HINT}>{methodNote(r, continuity, strings, fmt)}</p>
      <Decision reject={r.p < alpha}>
        {t(r.p < alpha ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      <p className="text-foreground text-sm">
        {t(strings.superiority, {
          percent: fmt(r.u1 / (r.n1 * r.n2), {
            style: "percent",
            maximumFractionDigits: 1,
          }),
          pairs: fmt(r.n1 * r.n2),
          rrb: fixed(r.rankBiserial, 2, fmt, false),
        })}
      </p>
      <RankTable fmt={fmt} r={r} strings={strings} x={x} y={y} />
      <Working heading={strings.howCalculated}>
        <p>
          R₁ = {n(r.rankSum1)}, R₂ = {n(r.rankSum2)} (n₁ = {fmt(r.n1)}, n₂ ={" "}
          {fmt(r.n2)}, N = {fmt(big)})
        </p>
        <p>
          U₁ = R₁ − n₁(n₁ + 1)/2 = {n(r.rankSum1)} − {n(half)} = {n(r.u1)}; U₂ =
          n₁n₂ − U₁ = {fmt(r.n1 * r.n2)} − {n(r.u1)} = {n(r.u2)}
        </p>
        <p>
          μ<sub>U</sub> = n₁n₂ / 2 = {n(r.mu)}; σ<sub>U</sub> = √(n₁n₂/12 × [(N
          + 1) − Σ(t³ − t) / (N(N − 1))]) = {n(r.sigma)}
        </p>
        <p>
          z = (U − μ<sub>U</sub>
          {cc}) / σ<sub>U</sub> = ({n(r.u)} − {n(r.mu)}
          {cc}) / {n(r.sigma)} = {n(r.z)}
        </p>
        <p>
          r = |z| / √N = {n(Math.abs(r.z))} / √{fmt(big)} = {n(r.r)}; r
          <sub>rb</sub> = (U₁ − U₂) / (n₁n₂) = {n(r.rankBiserial)}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={
          r.method === "exact" ? strings.apaNoteExact : strings.apaNoteNormal
        }
        parts={apa}
      />
    </section>
  );
}
